// src/components/Partners.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Array of logo file names in src/assets/partners/
const logos = [
  'microsoft.png',
  'googleref.png',
  'amazon.png'
];

const Partners = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section id="partners" className="section-padding bg-white overflow-hidden relative">
      {/* Spectacular Background Animation System */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Primary Morphing Circles */}
        <motion.div
          className="absolute -top-48 right-20 w-[500px] h-[500px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(circle, rgba(0,123,255,0.3) 0%, rgba(0,105,217,0.2) 50%, rgba(0,85,179,0.1) 100%)' // Updated rgba colors
          }}
          animate={{
            scale: [1, 1.6, 1.2, 1],
            rotate: [0, 360, 180, 0],
            x: [0, 120, -60, 0],
            y: [0, -80, 60, 0],
            borderRadius: ['50%', '40%', '60%', '50%']
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <motion.div
          className="absolute -bottom-48 left-20 w-[600px] h-[600px] rounded-full opacity-8"
          style={{
            background: 'radial-gradient(circle, rgba(0,105,217,0.3) 0%, rgba(0,85,179,0.2) 50%, rgba(0,123,255,0.1) 100%)' // Updated rgba colors
          }}
          animate={{
            scale: [1, 1.8, 1.4, 1],
            rotate: [360, 0, 270, 360],
            x: [0, -140, 80, 0],
            y: [0, 100, -70, 0],
            borderRadius: ['50%', '30%', '70%', '50%']
          }}
          transition={{
            duration: 40,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
        />

        {/* Dynamic Particle System */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${6 + Math.random() * 16}px`,
              height: `${6 + Math.random() * 16}px`,
              background: `radial-gradient(circle, ${
                i % 3 === 0 ? 'rgba(0,123,255,0.6)' : // Updated rgba to #007bff
                i % 3 === 1 ? 'rgba(0,105,217,0.6)' : // Updated rgba
                'rgba(0,85,179,0.6)' // Updated rgba
              } 0%, transparent 70%)`
            }}
            animate={{
              y: [-40, 40, -40],
              x: [-30, 30, -30],
              opacity: [0.1, 0.9, 0.1],
              scale: [0.3, 1.8, 0.3],
              rotate: [0, 360, 0]
            }}
            transition={{
              duration: 15 + Math.random() * 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 8
            }}
          />
        ))}

        {/* Animated Wave Pattern */}
        <motion.div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23007bff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` // Updated fill color to #007bff
          }}
          animate={{
            backgroundPosition: ['0px 0px', '120px 120px', '0px 0px']
          }}
          transition={{
            duration: 40,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <div className="container-custom relative z-10">
        {/* Enhanced Heading */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-20"
        >
          <motion.h2
            variants={itemVariants}
            whileInView={{
              scale: [0.8, 1.1, 1],
              opacity: [0, 1, 1],
              rotateX: [15, 0, 0]
            }}
            transition={{ duration: 1.2 }}
            className="text-4xl md:text-6xl font-bold text-neutral-900 mb-8" // Changed text-gray-900 to text-neutral-900
          >
            Our <span className="text-primary-600 relative"> {/* Changed text-blue-600 to text-primary-600 */}
              Service Partners
              <motion.div
                className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-primary-400 to-primary-600 rounded-full" // Updated gradient
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ duration: 1.2, delay: 0.5 }}
              />
            </span>
          </motion.h2>
          <motion.p
            variants={itemVariants}
            whileInView={{
              y: [50, 0],
              opacity: [0, 1],
              rotateX: [10, 0]
            }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-xl text-neutral-700 max-w-4xl mx-auto" // Changed text-gray-600 to text-neutral-700
          >
            We collaborate with industry leaders to deliver exceptional solutions and stay at the forefront of technology innovation.
          </motion.p>
        </motion.div>

        {/* Revolutionary Multi-Dimensional Scrolling System */}
        <motion.div
          variants={itemVariants}
          className="relative"
        >
          {/* Advanced Gradient Overlays with Pulse Effect */}
          <motion.div
            className="absolute left-0 top-0 w-32 h-full z-20 pointer-events-none"
            style={{
              background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 60%, rgba(255,255,255,0) 100%)'
            }}
            animate={{
              opacity: [0.9, 1, 0.9]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute right-0 top-0 w-32 h-full z-20 pointer-events-none"
            style={{
              background: 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 60%, rgba(255,255,255,0) 100%)'
            }}
            animate={{
              opacity: [0.9, 1, 0.9]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />

          {/* Multi-Row Scrolling System */}
          <div className="space-y-8">
            {/* First Row - Left to Right */}
            <div className="overflow-hidden">
              <motion.div
                className="flex space-x-12"
                animate={{
                  x: [0, -1200],
                }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 25,
                    ease: "linear",
                  },
                }}
              >
                {/* Triple the partners for smoother loop */}
                {[...logos, ...logos, ...logos].map((file, index) => (
                  <motion.div
                    key={`row1-${index}`}
                    className="flex-shrink-0 w-40 h-24 bg-white rounded-2xl shadow-xl border-2 border-neutral-100 flex items-center justify-center relative overflow-hidden" // Changed border-gray-100 to border-neutral-100
                    whileInView={{
                      scale: [0.8, 1.05, 1],
                      opacity: [0, 1],
                      rotateY: [15, 0, 0]
                    }}
                    transition={{
                      duration: 0.8,
                      delay: (index % 8) * 0.1
                    }}
                  >
                    {/* Animated Background - Removed group-hover class */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-primary-50 to-secondary-50 opacity-0" // Updated gradient
                      animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, 5, 0]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />

                    <motion.img
                      src={require(`../assets/partners/${file}`)}
                      alt={file.replace('.png', '')}
                      className="w-28 h-16 object-contain relative z-10"
                      animate={{
                        opacity: [0.9, 1, 0.9]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: index * 0.2
                      }}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Second Row - Right to Left */}
            <div className="overflow-hidden">
              <motion.div
                className="flex space-x-12"
                animate={{
                  x: [-1200, 0],
                }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 30,
                    ease: "linear",
                  },
                }}
              >
                {[...logos, ...logos, ...logos].map((file, index) => (
                  <motion.div
                    key={`row2-${index}`}
                    className="flex-shrink-0 w-40 h-24 bg-white rounded-2xl shadow-xl border-2 border-neutral-100 flex items-center justify-center relative overflow-hidden" // Changed border-gray-100 to border-neutral-100
                    whileInView={{
                      scale: [0.8, 1.05, 1],
                      opacity: [0, 1],
                      rotateY: [-15, 0, 0]
                    }}
                    transition={{
                      duration: 0.8,
                      delay: (index % 8) * 0.1
                    }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-l from-secondary-50 to-primary-50 opacity-0" // Updated gradient
                      animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, -5, 0]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />

                    <motion.img
                      src={require(`../assets/partners/${file}`)}
                      alt={file.replace('.png', '')}
                      className="w-28 h-16 object-contain relative z-10"
                      animate={{
                        opacity: [0.9, 1, 0.9]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: index * 0.2
                      }}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Stats Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
        >
          {[
            { number: '10+', label: 'Global Partners', color: 'blue', delay: 0 },
            { number: '10+', label: 'Certified Experts', color: 'indigo', delay: 0.2 },
            { number: '6+', label: 'Years Experience', color: 'purple', delay: 0.4 },
            { number: '100%', label: 'Success Rate', color: 'blue', delay: 0.6 }
          ].map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="relative"
              whileInView={{
                scale: [0.8, 1.1, 1],
                opacity: [0, 1]
              }}
              transition={{
                duration: 0.8,
                delay: stat.delay
              }}
            >
              <motion.div
                className="absolute inset-0 rounded-2xl opacity-0"
                style={{
                  background: `linear-gradient(135deg, ${
                    stat.color === 'blue' ? 'rgba(0,123,255,0.3), rgba(0,105,217,0.3)' : // Updated rgba colors
                    stat.color === 'indigo' ? 'rgba(0,123,255,0.3), rgba(0,105,217,0.3)' : // Changed indigo to primary shades
                    'rgba(0,123,255,0.3), rgba(0,105,217,0.3)' // Changed purple to primary shades
                  })`
                }}
                animate={{
                  scale: [1, 1.05, 1],
                  rotate: [0, 2, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

              <motion.div
                className={`text-4xl font-bold mb-2 ${
                  stat.color === 'blue' ? 'text-primary-600' : // Changed text-blue-600 to text-primary-600
                  stat.color === 'indigo' ? 'text-primary-600' : // Changed text-indigo-600 to text-primary-600
                  'text-primary-600' // Changed text-purple-600 to text-primary-600
                }`}
                animate={{
                  scale: [1, 1.15, 1],
                  textShadow: [
                    '0 0 0px rgba(0,123,255,0)', // Updated rgba to #007bff
                    '0 0 20px rgba(0,123,255,0.3)', // Updated rgba to #007bff
                    '0 0 0px rgba(0,123,255,0)' // Updated rgba to #007bff
                  ]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: stat.delay
                }}
              >
                {stat.number}
              </motion.div>

              <motion.p
                className="text-neutral-600 relative z-10" // Changed text-gray-600 to text-neutral-600
                animate={{
                  opacity: [0.8, 1, 0.8]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: stat.delay + 0.5
                }}
              >
                {stat.label}
              </motion.p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Partners;